import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, Send, Trash2, AlertCircle, Info, AlertTriangle, Bell } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    message: "",
    priority: "normal"
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/admin/announcements`, { withCredentials: true });
      setAnnouncements(res.data.announcements || []);
    } catch (err) {
      toast.error("Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error("Please fill in all fields");
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/announcements`, newAnnouncement, { withCredentials: true });
      toast.success(`Announcement sent to ${res.data.sent_to} partners`);
      setNewAnnouncement({ title: "", message: "", priority: "normal" });
      fetchAnnouncements();
    } catch (err) {
      toast.error("Failed to send announcement");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm("Delete this announcement?")) return;
    
    try {
      await axios.delete(`${API}/admin/announcements/${announcementId}`, { withCredentials: true });
      toast.success("Announcement deleted");
      fetchAnnouncements();
    } catch (err) {
      toast.error("Failed to delete announcement");
    }
  };

  const priorityConfig = {
    low: { icon: Info, color: "text-blue-400", bg: "bg-blue-500/10" },
    normal: { icon: Bell, color: "text-slate-400", bg: "bg-slate-500/10" },
    high: { icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-500/10" },
    urgent: { icon: AlertCircle, color: "text-red-400", bg: "bg-red-500/10" }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Megaphone className="h-7 w-7 text-emerald-500" />
          Announcements
        </h1>
        <p className="text-slate-400 mt-1">Send announcements to all partners</p>
      </div>

      {/* New Announcement Form */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Create Announcement</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSend} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Label className="text-slate-300">Title</Label>
                <Input
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Announcement title..."
                  className="mt-1 bg-slate-700 border-slate-600 text-white"
                  data-testid="announcement-title-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Priority</Label>
                <Select 
                  value={newAnnouncement.priority} 
                  onValueChange={(v) => setNewAnnouncement(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white" data-testid="announcement-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Message</Label>
              <Textarea
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Write your announcement message..."
                className="mt-1 bg-slate-700 border-slate-600 text-white min-h-[100px]"
                data-testid="announcement-message-input"
              />
            </div>
            <Button 
              type="submit" 
              disabled={sending}
              data-testid="send-announcement-btn"
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? "Sending..." : "Send to All Partners"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Announcements History */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Announcement History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
            </div>
          ) : announcements.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No announcements yet</p>
          ) : (
            <div className="space-y-4">
              {announcements.map((ann) => {
                const config = priorityConfig[ann.priority] || priorityConfig.normal;
                const Icon = config.icon;
                
                return (
                  <div 
                    key={ann.announcement_id}
                    className={`p-4 rounded-lg border border-slate-700 ${config.bg}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Icon className={`h-5 w-5 mt-0.5 ${config.color}`} />
                        <div>
                          <h3 className="font-semibold text-white">{ann.title}</h3>
                          <p className="text-slate-300 mt-1 text-sm whitespace-pre-wrap">{ann.message}</p>
                          <p className="text-xs text-slate-500 mt-2">
                            {new Date(ann.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(ann.announcement_id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-announcement-${ann.announcement_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnnouncements;
